/**
 * Composes the next persisted `ViewVisibility` for one visibility-matrix cell
 * write. Consumed by the matrix grid; step 1's popover editor keeps its own
 * merge path. `writePool` MUST be the `writePool` returned by
 * `useViewVisibilityPool` — the full organization union — never the contextual
 * display pool, nor the subset of columns the grid renders: hiding a view on an
 * object shown everywhere materializes the complement from this pool, so a
 * narrowed pool silently hides the object in every omitted view. Stored ids outside the pool are
 * locked and survive every write. `visibilityTypes` collapses to `undefined`
 * exactly when the next state shows the object in the ENTIRE pool with no
 * locked ids, restoring the shown-in-any-future-view default — the write
 * site must persist that as a field delete, not a merge of `undefined`.
 * Hiding a view clears its Member Rows entries, mirroring the step-1 editor.
 */
import type { VisibilityTypeId } from '#types';
import type { MemberRowsScope, ViewVisibility, VisibilityCellWrite } from './ViewVisibility.types';

const nextMemberHiddenTypes = (
    list: VisibilityTypeId[] | undefined,
    viewId: VisibilityTypeId,
    hidden: boolean,
): VisibilityTypeId[] | undefined => {
    const without = (list ?? []).filter((id) => id !== viewId);
    if (hidden) return [...without, viewId];
    return list === undefined ? undefined : without;
};

export const composeVisibilityCellWrite = ({
    current,
    writePool,
    viewId,
    next,
}: {
    current: ViewVisibility;
    writePool: readonly VisibilityTypeId[];
    viewId: VisibilityTypeId;
    next: VisibilityCellWrite;
}): ViewVisibility => {
    if (!writePool.includes(viewId)) {
        throw new Error(`composeVisibilityCellWrite: view '${viewId}' is not in the write pool`);
    }
    const poolSet = new Set(writePool);
    const stored = current.visibilityTypes ?? [];
    const lockedIds = stored.filter((id) => !poolSet.has(id));
    const shownPool = stored.length === 0 ? [...writePool] : stored.filter((id) => poolSet.has(id));
    const nextShownPool = next.shown
        ? shownPool.includes(viewId)
            ? shownPool
            : [...shownPool, viewId]
        : shownPool.filter((id) => id !== viewId);
    if (nextShownPool.length === 0 && lockedIds.length === 0) {
        throw new Error(
            'composeVisibilityCellWrite: hiding the last shown view would flip the object to shown-everywhere',
        );
    }
    const nextShownSet = new Set(nextShownPool);
    const coversEntirePool = writePool.every((id) => nextShownSet.has(id));
    const visibilityTypes = coversEntirePool && lockedIds.length === 0
        ? undefined
        : Array.from(new Set([...nextShownPool, ...lockedIds]));
    if (next.memberRows === undefined) return { visibilityTypes };
    const scope: MemberRowsScope = next.shown ? next.memberRows : 'none';
    return {
        visibilityTypes,
        kitMemberHiddenTypes: nextMemberHiddenTypes(
            current.kitMemberHiddenTypes,
            viewId,
            scope === 'kit' || scope === 'both',
        ),
        groupMemberHiddenTypes: nextMemberHiddenTypes(
            current.groupMemberHiddenTypes,
            viewId,
            scope === 'group' || scope === 'both',
        ),
    };
};
